import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "@/App.css";
import axios from "axios";
import AeonLoader, { AeonAccessGate } from "./components/AeonLoader";
import { bypassSplash, prefetchResearch } from "./researchBoot";
import VoiceConversation from "./components/VoiceConversation";
import ArenaShell from "./components/arena/ArenaShell";
import AeonHub from "./components/hub/AeonHub";
import AeonConsolePreview from "./components/console/AeonConsolePreview";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;
const WS_URL = BACKEND_URL
  ? `${BACKEND_URL.replace("https://", "wss://").replace("http://", "ws://")}/ws`
  : "";

// Inject API key into all native fetch() calls to /api routes
const _origFetch = window.fetch;
window.fetch = (url, options = {}) => {
  const apiKey = process.env.REACT_APP_API_KEY;
  if (apiKey && typeof url === "string" && url.includes("/api")) {
    options = { ...options, headers: { "X-API-Key": apiKey, ...options.headers } };
  }
  return _origFetch(url, options);
};

// Set API key on axios defaults
axios.defaults.headers.common["X-API-Key"] = process.env.REACT_APP_API_KEY || "";

const requestNotificationPermission = async () => {
  if ("Notification" in window && Notification.permission === "default") {
    await Notification.requestPermission();
  }
};

const showNotification = (title, body) => {
  if ("Notification" in window && Notification.permission === "granted") {
    new Notification(title, {
      body,
      icon: "/favicon.ico",
      tag: "aeon-alert",
      requireInteraction: false,
    });
  }
};

const playNotificationSound = () => {
  try {
    const audio = new Audio("data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2teleS0AGI/L6cxxOwMzhNCnYSQDP4LU/Yl2KAIUdrz/m38tBhZzt/+fgy0GF3K1/6OFLQYZZLL/poYtBRlks/+nhiwFF2S0/6mIKwUWZLX/qogrBRZktf+qiCsFGGW2/6mIKwQYZrf/qIgrBBhmuf+oiCsEGGa5/6eIKwQYZ7r/pogrBBhnu/+liCsEGGi8/6SIKwQYaL3/pIgrBBhovv+jiCsEGWm//6KIKwQZar//oYgrBBlqwP+hiCsEGWvB/6CIKwQZa8L/oIgrBBlrwv+fiCsEGWvD/5+IKwQZa8T/n4grBBlsxf+eiCsEGWzF/56IKwQZbcb/nYgrBBltx/+diCsEGW3H/52IKwQZbcj/nYgrBBltx/+diCsEGW3I/5yIKwQZbcn/nIgrBBltx/+diCsEGW3I/52IKwMZbsn/nIgrBBltx/+diCsE");
    audio.volume = 0.3;
    audio.play();
  } catch {
    // Browsers can block audio before user interaction.
  }
};

const normalizeWarnings = ({ system, portfolio, governance, fw }) => {
  const services = Object.values(system?.services || {});
  const degradedCount = services.filter((service) => service.status && service.status !== "healthy").length;
  const states = governance?.states || [];
  const restrictedEngines = states.filter((state) =>
    ["restricted", "sandbox", "disabled"].includes(String(state.current_recommendation || "").toLowerCase())
  );
  const hottestAccount = [...(portfolio?.accounts_list || [])].sort(
    (a, b) => Number(b.portfolio_heat || 0) - Number(a.portfolio_heat || 0)
  )[0];

  return {
    warnings: [
      degradedCount ? `${degradedCount} service(s) degraded` : null,
      hottestAccount ? `${hottestAccount.account_id} at ${(Number(hottestAccount.portfolio_heat || 0) * 100).toFixed(1)}% heat` : null,
      restrictedEngines.length ? `${restrictedEngines.length} engine(s) constrained` : null,
      fw?.malformed_rejects_30d ? `${fw.malformed_rejects_30d} malformed rejects in last 30d` : null,
    ].filter(Boolean).slice(0, 5),
    summary: {
      systemOverall: system?.overall || "unknown",
      degradedCount,
      portfolioHeat: portfolio?.aggregate_portfolio_heat || 0,
      totalOpenPositions: portfolio?.total_open_positions || 0,
      fwStatus: fw?.status || "unknown",
      fwTier: fw?.health_7d?.tier || "n/a",
      fwScore: fw?.health_7d?.score,
      malformedRejects: fw?.malformed_rejects_30d || 0,
      restrictedEngines: restrictedEngines.length,
      hottestAccount: hottestAccount?.account_id || "n/a",
    },
  };
};

const SESSION_KEY = "aeon_auth_token";

function App() {
  const consolePreview = new URLSearchParams(window.location.search).get("console") === "1";
  const [unlocked, setUnlocked] = useState(() => !!sessionStorage.getItem(SESSION_KEY));
  const [minimumBootComplete, setMinimumBootComplete] = useState(false);
  const skipSplash = bypassSplash(window.location.pathname, sessionStorage);
  const [arenaMode, setArenaMode] = useState(true);
  const [stats, setStats] = useState(null);
  const [messages, setMessages] = useState([]);
  const [botStatus, setBotStatus] = useState(null);
  const [marketData, setMarketData] = useState(null);
  const [tradingStats, setTradingStats] = useState(null);
  const [systemHealth, setSystemHealth] = useState(null);
  const [portfolioSummary, setPortfolioSummary] = useState(null);
  const [governanceSummary, setGovernanceSummary] = useState(null);
  const [fwSummary, setFwSummary] = useState(null);
  const [operatorSummary, setOperatorSummary] = useState(null);
  const [operatorWarnings, setOperatorWarnings] = useState([]);
  const [btcPrice, setBtcPrice] = useState(null);
  const [openPositions, setOpenPositions] = useState(0);
  const [researcherData, setResearcherData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const [unreadAlerts, setUnreadAlerts] = useState(0);
  const [regimeBanner, setRegimeBanner] = useState(null);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [showVoice, setShowVoice] = useState(false);
  const wsRef = useRef(null);

  const fetchData = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setRefreshing(true);
    try {
      // The shell should always become usable even when one optional feed is
      // slow. Promise.allSettled only resolves after every request settles, so
      // each boot request needs a finite timeout. Space the calls as well: the
      // API intentionally rate-limits bursts, and launching all 16 together
      // made otherwise healthy feeds look unavailable.
      let bootSlot = 0;
      const bootGet = (url) => {
        const delay = bootSlot++ * 250;
        return new Promise((resolve) => window.setTimeout(resolve, delay))
          .then(() => axios.get(url, { timeout: 15000 }));
      };
      const [
        statsRes,
        messagesRes,
        testRes,
        marketRes,
        tradingRes,
        systemRes,
        portfolioRes,
        govRes,
        fwRes,
        positionsRes,
        researcherStatusRes,
        researcherHurstRes,
        researcherEnsembleRes,
        researcherTcnRes,
        researcherCalibRes,
        researcherStratsRes,
      ] = await Promise.allSettled([
        bootGet(`${API}/bot/stats`),
        bootGet(`${API}/bot/messages?limit=30`),
        bootGet(`${API}/bot/test`),
        bootGet(`${API}/okx/live`),
        bootGet(`${API}/trading/summary`),
        bootGet(`${API}/system/health`),
        bootGet(`${API}/portfolio/summary`),
        bootGet(`${API}/engine/governance`),
        bootGet(`${API}/engine/fw_v2/report`),
        bootGet(`${API}/trading/v2/open`),
        bootGet(`${API}/researcher/status`),
        bootGet(`${API}/researcher/hurst`),
        bootGet(`${API}/researcher/ensemble`),
        bootGet(`${API}/researcher/tcn_votes`),
        bootGet(`${API}/researcher/calibration`),
        bootGet(`${API}/researcher/strategies`),
      ]);

      if (statsRes.status === "fulfilled") setStats(statsRes.value.data);
      if (messagesRes.status === "fulfilled") setMessages(messagesRes.value.data);
      if (testRes.status === "fulfilled") setBotStatus(testRes.value.data);
      if (tradingRes.status === "fulfilled") setTradingStats(tradingRes.value.data);

      if (marketRes.status === "fulfilled") {
        const market = marketRes.value.data;
        setMarketData(market);
        const btcSymbol = market?.symbols?.find((symbol) => ["BTC/USDT", "BTCUSDT"].includes(symbol.symbol));
        if (btcSymbol?.price) setBtcPrice(btcSymbol.price);
      }

      const system = systemRes.status === "fulfilled" ? systemRes.value.data : null;
      const portfolio = portfolioRes.status === "fulfilled" ? portfolioRes.value.data : null;
      const governance = govRes.status === "fulfilled" ? govRes.value.data : null;
      const fw = fwRes.status === "fulfilled" ? fwRes.value.data : null;

      if (system) setSystemHealth(system);
      if (portfolio) setPortfolioSummary(portfolio);
      if (governance) setGovernanceSummary(governance);
      if (fw) setFwSummary(fw);

      if (system || portfolio || governance || fw) {
        const normalized = normalizeWarnings({ system, portfolio, governance, fw });
        setOperatorWarnings(normalized.warnings);
        setOperatorSummary(normalized.summary);
      }

      if (positionsRes.status === "fulfilled") {
        const payload = positionsRes.value.data;
        setOpenPositions(payload?.total_open ?? (Array.isArray(payload) ? payload.length : 0));
      }

      // Researcher data — merge all sub-requests into one object
      const rStatus = researcherStatusRes.status === "fulfilled" ? researcherStatusRes.value.data : null;
      const rHurst = researcherHurstRes.status === "fulfilled" ? researcherHurstRes.value.data : null;
      const rEnsemble = researcherEnsembleRes.status === "fulfilled" ? researcherEnsembleRes.value.data : null;
      const rTcn = researcherTcnRes.status === "fulfilled" ? researcherTcnRes.value.data : null;
      const rCalib = researcherCalibRes.status === "fulfilled" ? researcherCalibRes.value.data : null;
      const rStrats = researcherStratsRes.status === "fulfilled" ? researcherStratsRes.value.data : null;
      setResearcherData({
        ...(rStatus || {}),
        hurst: rHurst,
        ensemble: rEnsemble,
        tcn: rTcn ? { ...rTcn.votes?.["BTC/USDT"], sharpe: rTcn.sharpe, alpha9: rTcn.alpha9, p_hat: rTcn.p_hat, live: rTcn.live } : null,
        calibration: rCalib,
        strategies: rStrats,
      });
    } catch (error) {
      console.error("AEON Hub data adapter failed:", error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(() => fetchData({ quiet: true }), 15000);
    return () => clearInterval(interval);
  }, [fetchData]);

  useEffect(() => {
    if (!unlocked) {
      setMinimumBootComplete(false);
      return undefined;
    }
    if (/^\/research(?:\/|$)/.test(window.location.pathname)) prefetchResearch();
    if (skipSplash) { setMinimumBootComplete(true); return undefined; }
    const timer = window.setTimeout(
      () => setMinimumBootComplete(true),
      consolePreview ? 120 : 2600,
    );
    return () => window.clearTimeout(timer);
  }, [unlocked, consolePreview, skipSplash]);

  useEffect(() => {
    if (unlocked && !loading && minimumBootComplete) {
      try { sessionStorage.setItem("aeon_boot_seen", "1"); } catch { /* optional storage */ }
    }
  }, [unlocked, loading, minimumBootComplete]);

  useEffect(() => {
    requestNotificationPermission().then(() => {
      if ("Notification" in window && Notification.permission === "granted") setNotificationsEnabled(true);
    });
  }, []);

  useEffect(() => {
    if (!WS_URL) return undefined;

    let wsRetryDelay = 1000;
    let retryTimeout = null;

    const connectWebSocket = () => {
      try {
        wsRef.current = new WebSocket(WS_URL);

        wsRef.current.onopen = () => {
          wsRetryDelay = 1000;
          setWsConnected(true);
        };

        wsRef.current.onmessage = (event) => {
          const data = JSON.parse(event.data);

          if (data.type === "alert") {
            setUnreadAlerts((value) => value + 1);
            if (notificationsEnabled) {
              playNotificationSound();
              showNotification(
                `${String(data.alert_type || "alert").replace("_", " ").toUpperCase()}: ${String(data.symbol || "AEON").replace("/USDT", "")}`,
                String(data.message || "New AEON alert").substring(0, 100)
              );
            }
          }

          if (data.type === "regime_change") {
            setRegimeBanner({ from: data.from_regime, to: data.to_regime, H: data.H });
            if (notificationsEnabled) {
              playNotificationSound();
              showNotification("Regime Change", data.message);
            }
            setTimeout(() => setRegimeBanner(null), 8000);
          }
        };

        wsRef.current.onclose = () => {
          setWsConnected(false);
          wsRetryDelay = Math.min(wsRetryDelay * 2, 30000);
          retryTimeout = setTimeout(connectWebSocket, wsRetryDelay);
        };

        wsRef.current.onerror = () => {};
      } catch {
        wsRetryDelay = Math.min(wsRetryDelay * 2, 30000);
        retryTimeout = setTimeout(connectWebSocket, wsRetryDelay);
      }
    };

    connectWebSocket();
    return () => {
      if (retryTimeout) clearTimeout(retryTimeout);
      if (wsRef.current) wsRef.current.close();
    };
  }, [notificationsEnabled]);

  const actions = useMemo(() => ({
    refresh: () => fetchData(),
    resumeTrading: async () => {
      await axios.post(`${API}/trading/toggle?active=true`);
      await fetchData();
    },
    pauseTrading: async () => {
      await axios.post(`${API}/trading/toggle?active=false`);
      await fetchData();
    },
    runScan: async () => fetchData(),
    openVoice: () => setShowVoice(true),
    clearAlerts: () => setUnreadAlerts(0),
    toggleNotifications: async () => {
      if (notificationsEnabled) {
        setNotificationsEnabled(false);
        return;
      }
      await requestNotificationPermission();
      setNotificationsEnabled("Notification" in window && Notification.permission === "granted");
    },
  }), [fetchData, notificationsEnabled]);

  if (!unlocked) {
    return (
      <AeonAccessGate
        onUnlock={(token) => {
          sessionStorage.setItem(SESSION_KEY, token);
          setUnlocked(true);
        }}
      />
    );
  }

  if (!skipSplash && ((!consolePreview && loading) || !minimumBootComplete)) {
    return <AeonLoader message="Assembling market intelligence" />;
  }

  const sharedProps = {
    stats,
    botStatus,
    tradingStats,
    marketData,
    messages,
    operatorSummary,
    operatorWarnings,
    portfolioSummary,
    governanceSummary,
    systemHealth,
    fwSummary,
    openPositions,
    btcPrice,
    wsConnected,
    unreadAlerts,
    regimeBanner,
    notificationsEnabled,
    refreshing,
    actions,
    researcherData,
  };

  return (
    <div className="min-h-screen bg-[#05070d] text-white">
      {consolePreview ? (
        <AeonConsolePreview
          {...sharedProps}
          onExit={() => {
            const url = new URL(window.location.href);
            url.searchParams.delete("console");
            window.location.assign(url.toString());
          }}
        />
      ) : arenaMode ? (
        <ArenaShell
          {...sharedProps}
          onLeaveArena={() => setArenaMode(false)}
        />
      ) : (
        <AeonHub
          {...sharedProps}
          onEnterArena={() => setArenaMode(true)}
        />
      )}

      {showVoice ? <VoiceConversation onClose={() => setShowVoice(false)} /> : null}
    </div>
  );
}

export default App;
